import { ChevronDown, ChevronUp } from 'lucide-react';
import { useRef, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { IconButton } from '../components/IconButton.js';
import { cn } from '../lib/cn.js';

export interface BottomPanelTab {
  id: string;
  label: string;
  /** Mono count badge. */
  count?: number | string;
  /** Tone dot before the label (running jobs, failures). */
  tone?: 'ok' | 'warn' | 'brand' | 'info';
}

export interface BottomPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Height in px when open. */
  height: number;
  onResize: (height: number) => void;
  min?: number;
  max?: number;
  tabs: BottomPanelTab[];
  activeTab: string;
  onTabChange: (id: string) => void;
  /** Right side of the tab strip (filters, clear, close). */
  rightSlot?: ReactNode;
  children: ReactNode;
  /** Accessible name. Default `Jobs panel`. */
  label?: string;
  className?: string;
}

export const BOTTOM_PANEL_COLLAPSED_HEIGHT = 32;

/**
 * Collapsible, resizable bottom panel (jobs / diagnostics). The tab strip stays visible when
 * collapsed; the drag handle on the top edge and arrow keys resize it.
 */
export function BottomPanel({ open, onOpenChange, height, onResize, min = 120, max = 600, tabs, activeTab, onTabChange, rightSlot, children, label = 'Jobs panel', className }: BottomPanelProps) {
  const startRef = useRef<{ y: number; h: number } | null>(null);
  const clamp = (v: number) => Math.round(Math.min(max, Math.max(min, v)));

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    startRef.current = { y: e.clientY, h: height };
    if (!open) onOpenChange(true);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const s = startRef.current;
    if (!s) return;
    onResize(clamp(s.h + (s.y - e.clientY)));
  };
  const endDrag = (e: PointerEvent<HTMLDivElement>) => {
    startRef.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };
  const onHandleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowUp') onResize(clamp(height + 16));
    else if (e.key === 'ArrowDown') onResize(clamp(height - 16));
    else if (e.key === 'Home') onResize(max);
    else if (e.key === 'End') onResize(min);
    else if (e.key === 'Enter' || e.key === ' ') onOpenChange(!open);
    else return;
    e.preventDefault();
  };
  const onTabKey = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    if (next === null) return;
    e.preventDefault();
    const t = tabs[next];
    if (t) {
      onTabChange(t.id);
      (e.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
    }
  };

  return (
    <section
      aria-label={label}
      data-open={open || undefined}
      style={{ height: open ? height : BOTTOM_PANEL_COLLAPSED_HEIGHT }}
      className={cn('relative flex shrink-0 flex-col border-t border-line bg-panel', className)}
    >
      <div
        role="separator"
        aria-label={`Resize ${label.toLowerCase()}`}
        aria-orientation="horizontal"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={open ? height : 0}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onHandleKey}
        className="group absolute -top-1 left-0 right-0 z-10 h-2 cursor-row-resize touch-none outline-none"
      >
        <span aria-hidden className="absolute inset-x-0 top-1 h-px bg-brand opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none" />
      </div>
      <div className="flex h-8 shrink-0 items-stretch justify-between gap-2 border-b border-hair pr-2">
        {/* Labels never wrap inside the 32px strip; the list scrolls sideways when the panel is too narrow for them. */}
        <div role="tablist" aria-label={`${label} tabs`} className="scrollbar-thin flex min-w-0 items-stretch overflow-x-auto">
          {tabs.map((t, i) => {
            const active = t.id === activeTab;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                tabIndex={active ? 0 : -1}
                onClick={() => {
                  onTabChange(t.id);
                  if (!open) onOpenChange(true);
                }}
                onKeyDown={(e) => onTabKey(e, i)}
                className={cn('relative flex shrink-0 items-center gap-2 whitespace-nowrap px-3 font-mono text-2xs uppercase tracking-[0.18em] transition-colors focus-visible:outline-offset-[-1px]', active ? 'text-ink' : 'text-mute hover:text-ink')}
              >
                {t.tone ? <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', t.tone === 'ok' ? 'bg-ok' : t.tone === 'warn' ? 'bg-warn' : t.tone === 'info' ? 'bg-info' : 'bg-brand')} /> : null}
                {t.label}
                {t.count !== undefined ? <span className="rounded-sm bg-bg px-1 py-px tabular-nums text-mute">{t.count}</span> : null}
                {active ? <span aria-hidden className="absolute inset-x-2 bottom-0 h-[2px] bg-brand" /> : null}
              </button>
            );
          })}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {rightSlot}
          <IconButton icon={open ? ChevronDown : ChevronUp} label={open ? 'Collapse panel' : 'Expand panel'} variant="ghost" size="sm" onClick={() => onOpenChange(!open)} aria-expanded={open} />
        </div>
      </div>
      {open ? (
        <div role="tabpanel" className="scrollbar-thin min-h-0 flex-1 overflow-auto">
          {children}
        </div>
      ) : null}
    </section>
  );
}
